import type {Object3D} from "three";
import type {PointDouble} from "../../../../../../../generated/api/base";
import {MarkupType, Permission} from "../../../../../../../generated/api/base";
import type {IBox2} from "../../../../../../../utils/THREEUtils";
import {THREEUtils} from "../../../../../../../utils/THREEUtils";
import {curveHeight, highlightRadius} from "../../elements3d/markups/MarkupStaticElements";

export const HANDLE_HIT_SIZE_PX = 16;
export const SCALE_HANDLE_SIZE_PX = 16;
export const EDIT_POINT_HANDLE_SIZE_PX = 16;
export const ROTATION_HANDLE_SIZE_PX = 28;
export const GROUP_ROTATION_HANDLE_SIZE_PX = 32;
const ROTATION_HANDLE_ICON_GAP_PX = 4;
const ROTATION_HANDLE_SECTOR_OUTER_PADDING_PX = 2;
const ROTATION_HANDLE_CORNER_INSET_AXIS_PX = 8;
const SELECTION_HANDLE_SAME_ANCHOR_THRESHOLD_PX = 4;
export const ROTATION_HANDLE_SECTOR_SEGMENTS = 40;
/** The corners of standardXyicon.glb are rounded with this radius (relative to the xyicon's size), approximated by this many segments */
export const STANDARD_XYICON_CORNER_RADIUS_RATIO = 0.1512277126312256;
const STANDARD_XYICON_CORNER_SEGMENTS = 4;

export type SelectionHandleKind = "box" | "line" | "rotate" | "groupRotate" | "scale" | "anchor" | "textBox";
export type SelectionHandleCorner = "topLeft" | "topRight" | "bottomRight" | "bottomLeft";
export type SelectionHandleOwnerType = "group" | "xyicon" | "boundary" | "markup" | "markupText";
export type SelectionHandlePhase = "zone" | "trigger" | "handle";

export interface ISelectionHandleZone {
	center: PointDouble;
	width: number;
	height: number;
}

export interface ISelectionHandleDescriptor {
	id: string;
	kind: SelectionHandleKind;
	ownerType: SelectionHandleOwnerType;
	priority: number;
	zone: ISelectionHandleZone;
	corner?: SelectionHandleCorner;
	hoverVisible?: boolean;
}

interface ISelectionHandleRaycastCandidate {
	phase?: SelectionHandlePhase;
	kind?: SelectionHandleKind;
	ownerType?: SelectionHandleOwnerType;
	anchorCenter?: PointDouble;
	intersectPoint?: PointDouble;
}

export const selectionHandleCorners: SelectionHandleCorner[] = ["topLeft", "topRight", "bottomRight", "bottomLeft"];

export const V5LinearMarkupTypes: MarkupType[] = [
	MarkupType.Arrow,
	MarkupType.BidirectionalArrow,
	MarkupType.Line,
	MarkupType.DashedLine,
	MarkupType.LinearDistance,
];

export const V5LinearMarkupTypesWithIndependentTextBoxControls: MarkupType[] = [
	MarkupType.Arrow,
	MarkupType.BidirectionalArrow,
	MarkupType.Line,
	MarkupType.DashedLine,
];

export const V5IrregularMarkupTypes: MarkupType[] = [
	MarkupType.PencilDrawing,
	MarkupType.HighlightDrawing,
	MarkupType.NonlinearDistance,
	MarkupType.IrregularArea,
];

const selectionHandlePriorities: Record<SelectionHandleOwnerType, number> = {
	group: 100,
	xyicon: 30,
	markup: 20,
	markupText: 15,
	boundary: 10,
};
const selectionHandlePhasePriorities: Record<SelectionHandlePhase, number> = {
	handle: 2,
	trigger: 1,
	zone: 0,
};

export const getSelectionHandlePriority = (ownerType: SelectionHandleOwnerType) => selectionHandlePriorities[ownerType];
export const getSelectionHandlePhasePriority = (phase?: SelectionHandlePhase) => (phase ? (selectionHandlePhasePriorities[phase] ?? -1) : -1);
export const isRotateSelectionHandleActionType = (actionType?: string) => actionType === "rotate" || actionType === "rotateText";
export const isScaleSelectionHandleActionType = (actionType?: string) => actionType === "scale" || actionType === "scaleText";
export const isAnchorSelectionHandleActionType = (actionType?: string) => actionType === "anchor" || actionType === "moveTextAnchor";
export const getSelectionHandleHoverHandleId = (actionType: string | undefined, id: string, hoverHandleId?: string) =>
	isRotateSelectionHandleActionType(actionType) ? id : hoverHandleId;

export const doesRotateSelectionHandleHitStartAction = (
	actionType: string | undefined,
	phase?: SelectionHandlePhase,
	zoneStartsAction: boolean = false,
) => isRotateSelectionHandleActionType(actionType) && (phase !== "zone" || zoneStartsAction);

export const getVisibleRotationHandleId = (activeRotationHandleId: string | null, hoveredRotationHandleId: string | null) =>
	activeRotationHandleId ?? hoveredRotationHandleId ?? null;

export const getVisibleRotationHandleState = (
	activeRotationHandleId: string | null,
	hoveredRotationHandleId: string | null,
	availableHandleIds: Set<string>,
) => {
	const nextActiveRotationHandleId = activeRotationHandleId && availableHandleIds.has(activeRotationHandleId) ? activeRotationHandleId : null;
	const nextHoveredRotationHandleId = hoveredRotationHandleId && availableHandleIds.has(hoveredRotationHandleId) ? hoveredRotationHandleId : null;

	return {
		activeRotationHandleId: nextActiveRotationHandleId,
		hoveredRotationHandleId: nextHoveredRotationHandleId,
		visibleRotationHandleId: getVisibleRotationHandleId(nextActiveRotationHandleId, nextHoveredRotationHandleId),
	};
};

export const getSelectionHandleIntersectables = (container?: Object3D | null): Object3D[] => {
	if (!container?.children?.length) {
		return [];
	}

	return [...container.children];
};

export const getBoxRotationHandleIconRotation = (corner: SelectionHandleCorner, orientation: number = 0) => {
	const baseAngleByCorner: Record<SelectionHandleCorner, number> = {
		topLeft: 0,
		topRight: -Math.PI / 2,
		bottomRight: Math.PI,
		bottomLeft: Math.PI / 2,
	};

	return baseAngleByCorner[corner] + orientation;
};

export const getBoxRotationHandleIconCenter = (
	cornerPoint: PointDouble,
	corner: SelectionHandleCorner,
	offset: number,
	orientation: number = 0,
): PointDouble => {
	const baseOffsetByCorner: Record<SelectionHandleCorner, PointDouble> = {
		topLeft: {x: -Math.SQRT1_2, y: Math.SQRT1_2},
		topRight: {x: Math.SQRT1_2, y: Math.SQRT1_2},
		bottomRight: {x: Math.SQRT1_2, y: -Math.SQRT1_2},
		bottomLeft: {x: -Math.SQRT1_2, y: -Math.SQRT1_2},
	};
	const rotatedOffset =
		orientation === 0 ? baseOffsetByCorner[corner] : THREEUtils.getRotatedVertex(baseOffsetByCorner[corner], orientation, {x: 0, y: 0});

	return {
		x: cornerPoint.x + rotatedOffset.x * offset,
		y: cornerPoint.y + rotatedOffset.y * offset,
	};
};

export const getBoxRotationHandleInsetPoint = (
	cornerPoint: PointDouble,
	corner: SelectionHandleCorner,
	correctionMultiplier: number,
	cameraZoomLevel: number,
	orientation: number = 0,
): PointDouble => {
	const inset = getWorldSizeFromPixels(ROTATION_HANDLE_CORNER_INSET_AXIS_PX, correctionMultiplier, cameraZoomLevel);
	const baseInsetByCorner: Record<SelectionHandleCorner, PointDouble> = {
		topLeft: {x: inset, y: -inset},
		topRight: {x: -inset, y: -inset},
		bottomRight: {x: -inset, y: inset},
		bottomLeft: {x: inset, y: inset},
	};
	const rotatedInset =
		orientation === 0 ? baseInsetByCorner[corner] : THREEUtils.getRotatedVertex(baseInsetByCorner[corner], orientation, {x: 0, y: 0});

	return {
		x: cornerPoint.x + rotatedInset.x,
		y: cornerPoint.y + rotatedInset.y,
	};
};

/** Pushes the zone diagonally outward, so it only touches the box at the corner and doesn't cover the item's own hit area */
export const getBoxRotationHandleZoneCenter = (
	cornerPoint: PointDouble,
	corner: SelectionHandleCorner,
	zoneSize: number,
	orientation: number = 0,
): PointDouble => {
	const offset = zoneSize / 2;
	const baseOffsetByCorner: Record<SelectionHandleCorner, PointDouble> = {
		topLeft: {x: -offset, y: offset},
		topRight: {x: offset, y: offset},
		bottomRight: {x: offset, y: -offset},
		bottomLeft: {x: -offset, y: -offset},
	};
	const rotatedOffset =
		orientation === 0 ? baseOffsetByCorner[corner] : THREEUtils.getRotatedVertex(baseOffsetByCorner[corner], orientation, {x: 0, y: 0});

	return {
		x: cornerPoint.x + rotatedOffset.x,
		y: cornerPoint.y + rotatedOffset.y,
	};
};

/**
 * The box-corner hit areas are built in the corner's local frame: the origin is the (sharp) corner of the box, and the handle
 * lies in the +x/+y quadrant, while the box itself is in the -x/-y quadrant. The frame is rotated by 90° at the topLeft and
 * bottomRight corners compared to the box, so the radii along the box's own x and y axes have to be swapped there.
 */
export const getBoxRotationHandleCornerRadius = (corner: SelectionHandleCorner, boxSize: PointDouble, radiusRatio: number): PointDouble => {
	const radius = {
		x: boxSize.x * radiusRatio,
		y: boxSize.y * radiusRatio,
	};

	return corner === "topLeft" || corner === "bottomRight" ? {x: radius.y, y: radius.x} : radius;
};

/** The rounded corner of the box in the local frame, from where it leaves the box's local "top" edge to where it joins its local "right" edge */
const getRoundedBoxCornerOutline = (cornerRadius: PointDouble): PointDouble[] => {
	const outline: PointDouble[] = [];

	for (let i = STANDARD_XYICON_CORNER_SEGMENTS; i >= 0; --i) {
		const angle = (i / STANDARD_XYICON_CORNER_SEGMENTS) * (Math.PI / 2);

		outline.push({
			x: cornerRadius.x * (Math.cos(angle) - 1),
			y: cornerRadius.y * (Math.sin(angle) - 1),
		});
	}

	return outline;
};

/**
 * Counter-clockwise outline of the zone in the corner's local frame: a `size` x `size` square outside of the corner (static on the screen),
 * extended inward until it hugs the box's rounded corner (follows the zoom), so the two fit together without a gap or an overlap.
 * It's star-shaped around the origin, so it can be triangulated as a fan from there.
 */
export const getBoxRotationHandleZoneOutline = (size: number, cornerRadius: PointDouble): PointDouble[] => [
	{x: size, y: -cornerRadius.y},
	{x: size, y: size},
	{x: -cornerRadius.x, y: size},
	...getRoundedBoxCornerOutline(cornerRadius),
];

/** Same as the zone's outline, but with a quarter-circle of `radius` (static on the screen) on the outer side of the corner */
export const getBoxRotationHandleTriggerOutline = (radius: number, cornerRadius: PointDouble): PointDouble[] => {
	const outline: PointDouble[] = [{x: radius, y: -cornerRadius.y}];

	for (let i = 0; i <= ROTATION_HANDLE_SECTOR_SEGMENTS; ++i) {
		const angle = (i / ROTATION_HANDLE_SECTOR_SEGMENTS) * (Math.PI / 2);

		outline.push({
			x: radius * Math.cos(angle),
			y: radius * Math.sin(angle),
		});
	}

	outline.push({x: -cornerRadius.x, y: radius}, ...getRoundedBoxCornerOutline(cornerRadius));

	return outline;
};

export const getRotationHandleIconRotation = (anchor: PointDouble, iconCenter: PointDouble) => {
	let angle = Math.atan2(iconCenter.y - anchor.y, iconCenter.x - anchor.x) - (3 * Math.PI) / 4;

	while (angle <= -Math.PI) {
		angle += 2 * Math.PI;
	}

	while (angle > Math.PI) {
		angle -= 2 * Math.PI;
	}

	return angle;
};

export const getLinearRotationHandleIconCenter = (start: PointDouble, end: PointDouble, endpointIndex: 0 | 1, offset: number): PointDouble => {
	const dx = end.x - start.x;
	const dy = end.y - start.y;
	const length = Math.hypot(dx, dy) || 1;
	const sign = endpointIndex === 0 ? -1 : 1;
	const anchor = endpointIndex === 0 ? start : end;

	return {
		x: anchor.x + (dx / length) * sign * offset,
		y: anchor.y + (dy / length) * sign * offset,
	};
};

export const getLinearSelectionHandleRotation = (start: PointDouble, end: PointDouble) => Math.atan2(end.y - start.y, end.x - start.x);

export const getSelectionHandleResizeCursor = (corner: SelectionHandleCorner, orientation: number = 0) => {
	const baseAngleByCorner: Record<SelectionHandleCorner, number> = {
		topLeft: 135,
		topRight: 45,
		bottomRight: -45,
		bottomLeft: -135,
	};
	const normalizedAngle = (((baseAngleByCorner[corner] + (orientation * 180) / Math.PI) % 180) + 180) % 180;

	if (normalizedAngle < 22.5 || normalizedAngle >= 157.5) {
		return "ew-resize";
	}

	if (normalizedAngle < 67.5) {
		return "nesw-resize";
	}

	if (normalizedAngle < 112.5) {
		return "ns-resize";
	}

	return "nwse-resize";
};

export const getSelectionBoxBoundingBox = (center: PointDouble, size: PointDouble, orientation: number = 0, padding: number = 0): IBox2 => {
	const unrotatedBoundingBox = {
		min: {
			x: center.x - size.x / 2 - padding,
			y: center.y - size.y / 2 - padding,
		},
		max: {
			x: center.x + size.x / 2 + padding,
			y: center.y + size.y / 2 + padding,
		},
	};
	const rotatedCorners = selectionHandleCorners.map((corner) => getBoxCornerPoint(unrotatedBoundingBox, corner, orientation));

	return THREEUtils.calculateBox(rotatedCorners);
};

export const getSelectionBoxTopCenter = (center: PointDouble, size: PointDouble, orientation: number = 0): PointDouble => {
	const unrotatedPoint = {
		x: center.x,
		y: center.y + size.y / 2,
	};

	return orientation === 0 ? unrotatedPoint : THREEUtils.getRotatedVertex(unrotatedPoint, orientation, center);
};

export const mergeBoundingBoxes = (bboxes: IBox2[]): IBox2 | null => {
	if (bboxes.length === 0) {
		return null;
	}

	const merged = {
		min: {
			x: Infinity,
			y: Infinity,
		},
		max: {
			x: -Infinity,
			y: -Infinity,
		},
	};

	for (const bbox of bboxes) {
		merged.min.x = Math.min(merged.min.x, bbox.min.x);
		merged.min.y = Math.min(merged.min.y, bbox.min.y);
		merged.max.x = Math.max(merged.max.x, bbox.max.x);
		merged.max.y = Math.max(merged.max.y, bbox.max.y);
	}

	return merged;
};

export const getMarkupSelectionPadding = (
	type: MarkupType | null | undefined,
	correctionMultiplier: number,
	cameraZoomLevel: number = 1,
	lineThickness: number = 0,
) => {
	switch (type) {
		case MarkupType.Cloud:
			return curveHeight * correctionMultiplier;
		case MarkupType.HighlightDrawing:
			return highlightRadius * correctionMultiplier;
		case MarkupType.PencilDrawing:
		case MarkupType.NonlinearDistance:
		case MarkupType.IrregularArea:
			return getWorldSizeFromPixels(lineThickness / 2, correctionMultiplier, cameraZoomLevel);
		default:
			return 0;
	}
};

export const isV5LinearMarkupType = (type?: MarkupType | null) => !!type && V5LinearMarkupTypes.includes(type);
export const doesV5LinearMarkupSupportIndependentTextBoxControls = (type?: MarkupType | null) =>
	!!type && V5LinearMarkupTypesWithIndependentTextBoxControls.includes(type);

export const isV5IrregularMarkupType = (type?: MarkupType | null) => !!type && V5IrregularMarkupTypes.includes(type);

export const doesMarkupKeepEditButtonInV5 = (type?: MarkupType | null) => isV5IrregularMarkupType(type);

export const doesMarkupUseCustomSelectionOverlayInV5 = (type?: MarkupType | null) => type === MarkupType.Callout || isV5LinearMarkupType(type);

export const doesSpaceItemAllowUpdatePermission = (permission?: Permission | null) => (permission ?? Permission.View) >= Permission.Update;

export const doesV5SelectionHandleAllowRotationForPermission = (permission?: Permission | null) => doesSpaceItemAllowUpdatePermission(permission);

export const canSingleSelectedItemEnterEditMode = (spaceItemType?: string | null, permission?: Permission | null) => {
	if (spaceItemType === "markup") {
		return true;
	}

	if (spaceItemType === "boundary") {
		return doesSpaceItemAllowUpdatePermission(permission);
	}

	return false;
};

export const doesSingleSelectedItemShowEditButton = (
	isV5: boolean,
	spaceItemType?: string | null,
	markupType?: MarkupType | null,
	permission?: Permission | null,
) => {
	if (spaceItemType === "boundary") {
		return doesSpaceItemAllowUpdatePermission(permission);
	}

	if (spaceItemType === "markup") {
		return isV5 ? doesMarkupKeepEditButtonInV5(markupType) : markupType != null && markupType !== MarkupType.Photo360;
	}

	return false;
};

export const canStartV5SelectionHandleRotationForSelection = (itemsWithoutUpdatePermissionCount: number) => itemsWithoutUpdatePermissionCount === 0;

export const getWorldSizeFromPixels = (pixels: number, correctionMultiplier: number, cameraZoomLevel: number) =>
	(pixels * correctionMultiplier) / cameraZoomLevel;

export const getRotationHandleOffsetDistancePx = (iconSizePx: number) => HANDLE_HIT_SIZE_PX / 2 + iconSizePx / 2 + ROTATION_HANDLE_ICON_GAP_PX;

export const getRotationHandleSectorOuterRadiusPx = (iconSizePx: number) =>
	getRotationHandleOffsetDistancePx(iconSizePx) + iconSizePx / 2 + ROTATION_HANDLE_SECTOR_OUTER_PADDING_PX;

export const getRotationHandleOffsetDistance = (iconSizePx: number, correctionMultiplier: number, cameraZoomLevel: number) =>
	getWorldSizeFromPixels(getRotationHandleOffsetDistancePx(iconSizePx), correctionMultiplier, cameraZoomLevel);

export const getRotationHandleSectorOuterRadius = (iconSizePx: number, correctionMultiplier: number, cameraZoomLevel: number) =>
	getWorldSizeFromPixels(getRotationHandleSectorOuterRadiusPx(iconSizePx), correctionMultiplier, cameraZoomLevel);

export const getSelectionHandleSameAnchorThreshold = (correctionMultiplier: number, cameraZoomLevel: number) =>
	getWorldSizeFromPixels(SELECTION_HANDLE_SAME_ANCHOR_THRESHOLD_PX, correctionMultiplier, cameraZoomLevel);

export const getRotationHandleSectorStartAngle = (anchor: PointDouble, iconCenter: PointDouble) =>
	Math.atan2(iconCenter.y - anchor.y, iconCenter.x - anchor.x) - Math.PI / 4;

export const getBoxCornerPoint = (bbox: IBox2, corner: SelectionHandleCorner, orientation: number = 0): PointDouble => {
	const center = THREEUtils.getCenterOfBoundingBox(bbox);
	let point: PointDouble;

	switch (corner) {
		case "topLeft":
			point = {x: bbox.min.x, y: bbox.max.y};
			break;
		case "topRight":
			point = {x: bbox.max.x, y: bbox.max.y};
			break;
		case "bottomRight":
			point = {x: bbox.max.x, y: bbox.min.y};
			break;
		default:
			point = {x: bbox.min.x, y: bbox.min.y};
			break;
	}

	return orientation === 0 ? point : THREEUtils.getRotatedVertex(point, orientation, center);
};

export const createZone = (center: PointDouble, size: number): ISelectionHandleZone => ({
	center,
	width: size,
	height: size,
});

export const doSelectionHandleAnchorsMatch = (first?: PointDouble, second?: PointDouble, sameAnchorThreshold: number = 0) =>
	!!first && !!second && Math.hypot(first.x - second.x, first.y - second.y) <= sameAnchorThreshold;

export const doZonesOverlap = (first: ISelectionHandleZone, second: ISelectionHandleZone) =>
	Math.abs(first.center.x - second.center.x) * 2 < first.width + second.width &&
	Math.abs(first.center.y - second.center.y) * 2 < first.height + second.height;

export const suppressOverlappingSelectionHandles = (handles: ISelectionHandleDescriptor[]) => {
	const sorted = [...handles].sort((left, right) => right.priority - left.priority);
	const selected: ISelectionHandleDescriptor[] = [];

	for (const handle of sorted) {
		if (selected.every((existing) => !doZonesOverlap(existing.zone, handle.zone))) {
			selected.push(handle);
		}
	}

	return selected;
};

export const suppressSelectionHandlesWithSameAnchor = (
	handles: ISelectionHandleDescriptor[],
	sameAnchorThreshold: number,
	pinnedHandleIds: Set<string> = new Set<string>(),
) => {
	const sorted = [...handles].sort((left, right) => {
		const isLeftPinned = pinnedHandleIds.has(left.id);
		const isRightPinned = pinnedHandleIds.has(right.id);

		if (isLeftPinned !== isRightPinned) {
			return isLeftPinned ? -1 : 1;
		}

		return right.priority - left.priority;
	});
	const selected: ISelectionHandleDescriptor[] = [];

	for (const handle of sorted) {
		if (selected.every((existing) => !doSelectionHandleAnchorsMatch(existing.zone.center, handle.zone.center, sameAnchorThreshold))) {
			selected.push(handle);
		}
	}

	return selected;
};

export const compareSelectionHandleRaycastPriority = (
	left?: {
		phase?: SelectionHandlePhase;
		kind?: SelectionHandleKind;
		ownerType?: SelectionHandleOwnerType;
		anchorCenter?: PointDouble;
	},
	right?: {
		phase?: SelectionHandlePhase;
		kind?: SelectionHandleKind;
		ownerType?: SelectionHandleOwnerType;
		anchorCenter?: PointDouble;
	},
	sameAnchorThreshold: number = 0,
) => {
	const phasePriorityDifference = getSelectionHandlePhasePriority(left?.phase) - getSelectionHandlePhasePriority(right?.phase);

	if (phasePriorityDifference !== 0) {
		return phasePriorityDifference;
	}

	if (
		sameAnchorThreshold > 0 &&
		left?.anchorCenter &&
		right?.anchorCenter &&
		!doSelectionHandleAnchorsMatch(left.anchorCenter, right.anchorCenter, sameAnchorThreshold)
	) {
		return 0;
	}

	const isLeftRotationHandle = left?.kind === "rotate" || left?.kind === "groupRotate";
	const isRightRotationHandle = right?.kind === "rotate" || right?.kind === "groupRotate";

	if (isLeftRotationHandle && isRightRotationHandle && left?.ownerType && right?.ownerType) {
		return getSelectionHandlePriority(left.ownerType) - getSelectionHandlePriority(right.ownerType);
	}

	return 0;
};

export const compareSelectionHandleRaycastCandidates = (
	left?: ISelectionHandleRaycastCandidate,
	right?: ISelectionHandleRaycastCandidate,
	sameAnchorThreshold: number = 0,
) => {
	const priorityDifference = compareSelectionHandleRaycastPriority(left, right, sameAnchorThreshold);

	if (priorityDifference !== 0) {
		return priorityDifference;
	}

	if (
		left?.anchorCenter &&
		right?.anchorCenter &&
		left.intersectPoint &&
		right.intersectPoint &&
		!doSelectionHandleAnchorsMatch(left.anchorCenter, right.anchorCenter, sameAnchorThreshold)
	) {
		const leftDistanceSq = (left.anchorCenter.x - left.intersectPoint.x) ** 2 + (left.anchorCenter.y - left.intersectPoint.y) ** 2;
		const rightDistanceSq = (right.anchorCenter.x - right.intersectPoint.x) ** 2 + (right.anchorCenter.y - right.intersectPoint.y) ** 2;

		if (leftDistanceSq !== rightDistanceSq) {
			return rightDistanceSq - leftDistanceSq;
		}
	}

	return 0;
};
